import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, User, Camera, Phone, ChevronDown } from 'lucide-react';
import Footer from '../components/layout/footer';
import { useLanguage } from '../context/LanguageContext';
import { supabase } from '../lib/supabase';
import { countryCodes, defaultCountry, getPhonePlaceholder, parsePhone, type CountryCode } from '../lib/country-codes';

interface ShippingAddress {
  id: string;
  full_name: string;
  phone: string | null;
  address_line1: string;
  address_line2: string | null;
  city: string;
  region: string | null;
  postal_code: string | null;
  country: string | null;
  is_default: boolean;
}

const AVATAR_BUCKET = 'avatars';

export default function MyProfilePage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [tab, setTab] = useState<'profile' | 'addresses'>('profile');

  // Profile
  const [first_name, setFirstName] = useState('');
  const [last_name, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneLocal, setPhoneLocal] = useState('');
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>(defaultCountry);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [avatar_url, setAvatarUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Addresses
  const [addresses, setAddresses] = useState<ShippingAddress[]>([]);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [addressSaving, setAddressSaving] = useState(false);
  const [addressForm, setAddressForm] = useState({
    full_name: '',
    phone: '',
    address_line1: '',
    address_line2: '',
    city: '',
    region: '',
    postal_code: '',
    country: 'SN',
    is_default: false,
  });

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (showCountryDropdown && !target.closest('[data-country-dropdown]')) setShowCountryDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showCountryDropdown]);

  useEffect(() => {
    const load = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/login');
        return;
      }
      const [profileRes, addressesRes] = await Promise.all([
        supabase.from('profiles').select('first_name, last_name, email, phone, avatar_url').eq('id', session.user.id).single(),
        supabase.from('shipping_addresses').select('id, full_name, phone, address_line1, address_line2, city, region, postal_code, country, is_default').eq('profile_id', session.user.id).order('is_default', { ascending: false }),
      ]);
      if (profileRes.data) {
        const p = profileRes.data;
        setFirstName(p.first_name ?? '');
        setLastName(p.last_name ?? '');
        setEmail(p.email ?? session.user.email ?? '');
        const { country, local } = parsePhone(p.phone ?? '');
        setSelectedCountry(country);
        setPhoneLocal(local);
        setAvatarUrl(p.avatar_url ?? '');
      } else {
        setEmail(session.user.email ?? '');
      }
      if (!addressesRes.error) setAddresses(addressesRes.data || []);
      setLoading(false);
    };
    load();
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    setSaving(true);
    setSaved(false);
    const fullPhone = phoneLocal.trim() ? `${selectedCountry.dialCode}${phoneLocal.replace(/\s/g, '')}` : null;
    const { error } = await supabase
      .from('profiles')
      .update({
        first_name: first_name || null,
        last_name: last_name || null,
        email: email || null,
        phone: fullPhone,
        avatar_url: avatar_url || null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', session.user.id);
    setSaving(false);
    if (!error) setSaved(true);
  };

  const uploadAvatar = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const { data: { session } } = await supabase.auth.getSession();
    if (!file || !session?.user) return;
    setAvatarError(null);
    setAvatarUploading(true);
    const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
    const path = `${session.user.id}/avatar.${ext}`;
    const { error: uploadError } = await supabase.storage.from(AVATAR_BUCKET).upload(path, file, { upsert: true });
    if (uploadError) {
      setAvatarError(uploadError.message || "Erreur lors de l'envoi.");
      setAvatarUploading(false);
      e.target.value = '';
      return;
    }
    const { data: urlData } = supabase.storage.from(AVATAR_BUCKET).getPublicUrl(path);
    setAvatarUrl(urlData.publicUrl);
    await supabase.from('profiles').update({ avatar_url: urlData.publicUrl, updated_at: new Date().toISOString() }).eq('id', session.user.id);
    setAvatarUploading(false);
    e.target.value = '';
  };

  const saveAddress = async (e: React.FormEvent) => {
    e.preventDefault();
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    if (!addressForm.full_name.trim() || !addressForm.address_line1.trim() || !addressForm.city.trim()) return;
    setAddressSaving(true);
    if (addressForm.is_default) {
      await supabase.from('shipping_addresses').update({ is_default: false }).eq('profile_id', session.user.id);
    }
    const { error } = await supabase.from('shipping_addresses').insert({
      profile_id: session.user.id,
      full_name: addressForm.full_name.trim(),
      phone: addressForm.phone.trim() || null,
      address_line1: addressForm.address_line1.trim(),
      address_line2: addressForm.address_line2.trim() || null,
      city: addressForm.city.trim(),
      region: addressForm.region.trim() || null,
      postal_code: addressForm.postal_code.trim() || null,
      country: addressForm.country || 'SN',
      is_default: addressForm.is_default,
    });
    setAddressSaving(false);
    if (!error) {
      setAddressForm({ full_name: '', phone: '', address_line1: '', address_line2: '', city: '', region: '', postal_code: '', country: 'SN', is_default: false });
      setShowAddressForm(false);
      const { data } = await supabase.from('shipping_addresses').select('id, full_name, phone, address_line1, address_line2, city, region, postal_code, country, is_default').eq('profile_id', session.user.id).order('is_default', { ascending: false });
      if (data) setAddresses(data);
    }
  };

  const setDefaultAddress = async (id: string) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    await supabase.from('shipping_addresses').update({ is_default: false }).eq('profile_id', session.user.id);
    await supabase.from('shipping_addresses').update({ is_default: true }).eq('id', id);
    const { data } = await supabase.from('shipping_addresses').select('id, full_name, phone, address_line1, address_line2, city, region, postal_code, country, is_default').eq('profile_id', session.user.id).order('is_default', { ascending: false });
    if (data) setAddresses(data);
  };

  const formatAddress = (a: ShippingAddress) => {
    const parts = [a.address_line1, a.address_line2, a.postal_code, a.city, a.region, a.country].filter(Boolean);
    return parts.join(', ');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  const avatarDisplay = avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(first_name || last_name || 'U')}&background=333&color=fff&size=192`;

  return (
    <div className="min-h-screen bg-neutral-950 text-white">
      <div className="pt-24 md:pt-28 pb-20 max-w-2xl mx-auto px-6">
        <h1 className="font-['Sora'] text-2xl md:text-3xl font-light tracking-tight mb-8">
          {t('account.my_profile')}
        </h1>

        {/* Tabs */}
        <div className="flex gap-1 p-1 rounded-2xl bg-white/5 border border-white/10 mb-8">
          <button
            type="button"
            onClick={() => setTab('profile')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-['Rubik'] text-xs tracking-[0.15em] uppercase transition-colors ${tab === 'profile' ? 'bg-white text-black' : 'text-white/60 hover:text-white'}`}
          >
            <User className="h-4 w-4" />
            {t('account.my_profile')}
          </button>
          <button
            type="button"
            onClick={() => setTab('addresses')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-['Rubik'] text-xs tracking-[0.15em] uppercase transition-colors ${tab === 'addresses' ? 'bg-white text-black' : 'text-white/60 hover:text-white'}`}
          >
            <MapPin className="h-4 w-4" />
            {t('account.my_address')}
          </button>
        </div>

        {tab === 'profile' && (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Photo de profil : bouton Modifier / Ajouter */}
            <div className="flex flex-col items-center gap-4 mb-8">
              <div className="relative group">
                <img
                  src={avatarDisplay}
                  alt=""
                  className="w-28 h-28 md:w-32 md:h-32 rounded-full object-cover border-2 border-white/20"
                />
                {avatarUploading && (
                  <div className="absolute inset-0 rounded-full bg-black/60 flex items-center justify-center">
                    <span className="font-['Rubik'] text-xs text-white">...</span>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="hidden"
                  onChange={uploadAvatar}
                  aria-label={t('account.profile_photo')}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={avatarUploading}
                  className="absolute bottom-0 right-0 flex h-10 w-10 items-center justify-center rounded-full bg-white text-black border-2 border-neutral-950 shadow-lg hover:bg-neutral-100 transition-colors disabled:opacity-60"
                  aria-label={avatar_url ? t('account.change_photo') : t('account.add_photo')}
                >
                  <Camera className="h-5 w-5" />
                </button>
              </div>
              <p className="font-['Rubik'] text-xs text-white/50">
                {avatar_url ? t('account.change_photo') : t('account.add_photo')}
              </p>
              {avatarError && <p className="font-['Rubik'] text-sm text-red-400">{avatarError}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <label>
                <span className="block font-['Rubik'] text-xs tracking-widest uppercase text-white/50 mb-2">{t('account.first_name')}</span>
                <input type="text" value={first_name} onChange={(e) => setFirstName(e.target.value)} className="w-full px-4 py-3 font-['Rubik'] text-white bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-white/30" />
              </label>
              <label>
                <span className="block font-['Rubik'] text-xs tracking-widest uppercase text-white/50 mb-2">{t('account.last_name')}</span>
                <input type="text" value={last_name} onChange={(e) => setLastName(e.target.value)} className="w-full px-4 py-3 font-['Rubik'] text-white bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-white/30" />
              </label>
            </div>
            <label>
              <span className="block font-['Rubik'] text-xs tracking-widest uppercase text-white/50 mb-2">{t('contact.email')}</span>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-4 py-3 font-['Rubik'] text-white bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-white/30" />
            </label>
            <div className="mt-6">
              <span className="block font-['Rubik'] text-xs tracking-widest uppercase text-white/50 mb-2">{t('account.phone')}</span>
              <div className="flex gap-2" data-country-dropdown>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                    className="flex justify-center items-center px-3 h-12 bg-white/5 rounded-xl border border-white/10 hover:border-white/20 transition-colors"
                  >
                    <img
                      src={`https://flagcdn.com/w40/${selectedCountry.flag.toLowerCase()}.png`}
                      alt={selectedCountry.name}
                      className="object-cover w-8 h-6 rounded"
                    />
                    <ChevronDown className="ml-2 w-4 h-4 text-white/60" />
                  </button>
                  {showCountryDropdown && (
                    <div className="overflow-y-auto absolute left-0 top-full z-50 mt-2 w-72 max-h-60 bg-neutral-900 border border-white/10 rounded-xl shadow-xl">
                      {countryCodes.map((country) => (
                        <button
                          key={country.code}
                          type="button"
                          onClick={() => {
                            setSelectedCountry(country);
                            setShowCountryDropdown(false);
                          }}
                          className="flex items-center p-3 w-full transition-colors hover:bg-white/10 text-left"
                        >
                          <img
                            src={`https://flagcdn.com/w40/${country.flag.toLowerCase()}.png`}
                            alt={country.name}
                            className="object-cover mr-3 w-8 h-6 rounded"
                          />
                          <span className="flex-1 font-['Rubik'] text-sm text-white">{country.name}</span>
                          <span className="text-white/50">{country.dialCode}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex flex-1 items-center rounded-xl border border-white/10 focus-within:border-white/30 transition-colors">
                  <span className="flex items-center px-3 h-12 text-sm font-['Rubik'] text-white/70 bg-white/5 rounded-l-xl border-r border-white/10">
                    {selectedCountry.dialCode}
                  </span>
                  <div className="relative flex-1">
                    <Phone className="absolute left-3 top-1/2 w-4 h-4 text-white/40 -translate-y-1/2" />
                    <input
                      type="tel"
                      value={phoneLocal}
                      onChange={(e) => setPhoneLocal(e.target.value)}
                      placeholder={getPhonePlaceholder(selectedCountry.code)}
                      className="py-3 pr-4 pl-10 w-full font-['Rubik'] text-white bg-transparent rounded-r-xl border-0 focus:outline-none placeholder:text-white/30"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="pt-2">
              {saved && <p className="font-['Rubik'] text-sm text-emerald-400 mb-4">{t('account.saved')}</p>}
              <button type="submit" disabled={saving} className="w-full py-4 font-['Rubik'] text-xs tracking-[0.2em] uppercase text-white bg-white/10 border border-white/20 rounded-xl hover:bg-white hover:text-black transition-all duration-300 disabled:opacity-60">
                {saving ? '...' : t('account.save')}
              </button>
            </div>
          </form>
        )}

        {tab === 'addresses' && (
          <div className="space-y-6">
            {addresses.length === 0 && !showAddressForm ? (
              <div className="rounded-2xl border border-white/10 bg-white/5 p-10 text-center">
                <MapPin className="mx-auto h-12 w-12 text-white/30 mb-4" />
                <p className="font-['Rubik'] text-white/70">{t('account.no_address')}</p>
                <p className="mt-2 font-['Rubik'] text-sm text-white/50 mb-6">{t('account.add_address_anytime')}</p>
                <button type="button" onClick={() => setShowAddressForm(true)} className="inline-flex items-center gap-2 py-3 px-6 font-['Rubik'] text-xs tracking-[0.2em] uppercase text-white bg-white/10 border border-white/20 rounded-xl hover:bg-white hover:text-black transition-all">
                  {t('account.add_address')}
                </button>
              </div>
            ) : (
              <>
                <div className="flex justify-end">
                  <button type="button" onClick={() => setShowAddressForm(true)} className="inline-flex items-center gap-2 py-3 px-5 font-['Rubik'] text-xs tracking-[0.15em] uppercase text-white bg-white/10 border border-white/20 rounded-xl hover:bg-white hover:text-black transition-all">
                    {t('account.add_address')}
                  </button>
                </div>
                {showAddressForm && (
                  <form onSubmit={saveAddress} className="rounded-2xl border border-white/10 bg-white/5 p-6 space-y-4">
                    <h3 className="font-['Sora'] text-sm font-medium text-white mb-4">{t('account.add_address')}</h3>
                    <label>
                      <span className="block font-['Rubik'] text-xs tracking-widest uppercase text-white/50 mb-1">{t('account.full_name')}</span>
                      <input type="text" required value={addressForm.full_name} onChange={(e) => setAddressForm((f) => ({ ...f, full_name: e.target.value }))} className="w-full px-4 py-3 font-['Rubik'] text-white bg-neutral-950 border border-white/10 rounded-xl focus:outline-none focus:border-white/30" />
                    </label>
                    <label>
                      <span className="block font-['Rubik'] text-xs tracking-widest uppercase text-white/50 mb-1">{t('account.phone')}</span>
                      <input type="tel" value={addressForm.phone} onChange={(e) => setAddressForm((f) => ({ ...f, phone: e.target.value }))} className="w-full px-4 py-3 font-['Rubik'] text-white bg-neutral-950 border border-white/10 rounded-xl focus:outline-none focus:border-white/30" />
                    </label>
                    <label>
                      <span className="block font-['Rubik'] text-xs tracking-widest uppercase text-white/50 mb-1">{t('account.address_line1')}</span>
                      <input type="text" required value={addressForm.address_line1} onChange={(e) => setAddressForm((f) => ({ ...f, address_line1: e.target.value }))} className="w-full px-4 py-3 font-['Rubik'] text-white bg-neutral-950 border border-white/10 rounded-xl focus:outline-none focus:border-white/30" />
                    </label>
                    <label>
                      <span className="block font-['Rubik'] text-xs tracking-widest uppercase text-white/50 mb-1">{t('account.address_line2')}</span>
                      <input type="text" value={addressForm.address_line2} onChange={(e) => setAddressForm((f) => ({ ...f, address_line2: e.target.value }))} className="w-full px-4 py-3 font-['Rubik'] text-white bg-neutral-950 border border-white/10 rounded-xl focus:outline-none focus:border-white/30" />
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <label>
                        <span className="block font-['Rubik'] text-xs tracking-widest uppercase text-white/50 mb-1">{t('account.city')}</span>
                        <input type="text" required value={addressForm.city} onChange={(e) => setAddressForm((f) => ({ ...f, city: e.target.value }))} className="w-full px-4 py-3 font-['Rubik'] text-white bg-neutral-950 border border-white/10 rounded-xl focus:outline-none focus:border-white/30" />
                      </label>
                      <label>
                        <span className="block font-['Rubik'] text-xs tracking-widest uppercase text-white/50 mb-1">{t('account.postal_code')}</span>
                        <input type="text" value={addressForm.postal_code} onChange={(e) => setAddressForm((f) => ({ ...f, postal_code: e.target.value }))} className="w-full px-4 py-3 font-['Rubik'] text-white bg-neutral-950 border border-white/10 rounded-xl focus:outline-none focus:border-white/30" />
                      </label>
                    </div>
                    <label>
                      <span className="block font-['Rubik'] text-xs tracking-widest uppercase text-white/50 mb-1">{t('account.region')}</span>
                      <input type="text" value={addressForm.region} onChange={(e) => setAddressForm((f) => ({ ...f, region: e.target.value }))} className="w-full px-4 py-3 font-['Rubik'] text-white bg-neutral-950 border border-white/10 rounded-xl focus:outline-none focus:border-white/30" />
                    </label>
                    <label>
                      <span className="block font-['Rubik'] text-xs tracking-widest uppercase text-white/50 mb-1">{t('account.country')}</span>
                      <input type="text" value={addressForm.country} onChange={(e) => setAddressForm((f) => ({ ...f, country: e.target.value }))} className="w-full px-4 py-3 font-['Rubik'] text-white bg-neutral-950 border border-white/10 rounded-xl focus:outline-none focus:border-white/30" />
                    </label>
                    <label className="flex items-center gap-3">
                      <input type="checkbox" checked={addressForm.is_default} onChange={(e) => setAddressForm((f) => ({ ...f, is_default: e.target.checked }))} className="rounded border-white/30" />
                      <span className="font-['Rubik'] text-sm text-white/80">{t('account.default')}</span>
                    </label>
                    <div className="flex gap-3 pt-2">
                      <button type="submit" disabled={addressSaving} className="py-3 px-5 font-['Rubik'] text-xs tracking-[0.15em] uppercase text-white bg-white/20 rounded-xl hover:bg-white hover:text-black disabled:opacity-60">
                        {addressSaving ? '...' : t('account.save')}
                      </button>
                      <button type="button" onClick={() => setShowAddressForm(false)} className="py-3 px-5 font-['Rubik'] text-xs tracking-[0.15em] uppercase text-white/70 hover:text-white">
                        {t('common.cancel')}
                      </button>
                    </div>
                  </form>
                )}
                <ul className="space-y-4">
                  {addresses.map((a) => (
                    <li key={a.id} className="rounded-2xl border border-white/10 bg-white/5 p-6">
                      {a.is_default && (
                        <span className="inline-block text-xs font-['Rubik'] tracking-widest uppercase text-white/50 mb-2">{t('account.default')}</span>
                      )}
                      <p className="font-['Sora'] font-medium text-white">{a.full_name}</p>
                      {a.phone && <p className="font-['Rubik'] text-sm text-white/60 mt-1">{a.phone}</p>}
                      <p className="font-['Rubik'] text-white/80 mt-2">{formatAddress(a)}</p>
                      {!a.is_default && (
                        <button type="button" onClick={() => setDefaultAddress(a.id)} className="mt-3 font-['Rubik'] text-xs tracking-widest uppercase text-white/50 hover:text-white">
                          {t('account.set_default')}
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
